import { Receipt as InvoiceIcon } from "@mui/icons-material";
import type { FC } from "react";
import { useState } from "react";
import type {
  AddAdvanceCorrectionInvoiceRequestBody,
  AddAdvanceOrProformaInvoiceRequestBody,
  AddFinalCorrectionInvoiceRequestBody,
  AddFinalInvoiceRequestBody,
} from "../../../api/api";
import ButtonWithContextMenu from "../../components/buttonWithContextMenu/ButtonWithContextMenu";
import type { SavedOrder } from "../orderTypes";
import AdvanceOrProformaInvoiceDialog from "./AdvanceOrProformaInvoiceDialog";
import CorrectionInvoiceDialog from "./CorrectionInvoiceDialog";
import FinalCorrectionInvoiceDialog from "./FinalCorrectionInvoiceDialog";
import FinalInvoiceDialog from "./FinalInvoiceDialog";
import { useOrderInvoices } from "./useOrderInvoices";

interface Props {
  readonly order: Pick<SavedOrder, "id" | "start" | "currency" | "advance" | "invoices">;
}

const OrderInvoiceButton: FC<Props> = ({ order }) => {
  const {
    addAdvanceInvoice,
    addProformaInvoice,
    addAdvanceCorrectionInvoice,
    addFinalInvoice,
    addFinalCorrectionInvoice,
  } = useOrderInvoices(order);
  const [open, setOpen] = useState<"PROFORMA" | "ADVANCE" | "FINAL" | "CORRECTION" | "FINAL_CORRECTION" | null>(null);

  const hasFinalOrVatInvoice = order.invoices.some((i) => i.type === "FINAL" || i.type === "VAT");

  const handleSubmitAdvanceInvoice = async (request: AddAdvanceOrProformaInvoiceRequestBody) => {
    await addAdvanceInvoice(request);
    setOpen(null);
  };

  const handleSubmitProformaInvoice = async (request: AddAdvanceOrProformaInvoiceRequestBody) => {
    await addProformaInvoice(request);
    setOpen(null);
  };

  const handleSubmitCorrectionInvoice = async (body: AddAdvanceCorrectionInvoiceRequestBody) => {
    await addAdvanceCorrectionInvoice(body);
    setOpen(null);
  };

  const handleSubmitFinalInvoice = async (request: AddFinalInvoiceRequestBody) => {
    await addFinalInvoice(request);
    setOpen(null);
  };

  const handleSubmitFinalCorrectionInvoice = async (body: AddFinalCorrectionInvoiceRequestBody) => {
    await addFinalCorrectionInvoice(body);
    setOpen(null);
  };

  return (
    <>
      <ButtonWithContextMenu
        startIcon={<InvoiceIcon />}
        menuItems={[
          { name: "Faktura proforma", onClick: () => setOpen("PROFORMA") },
          { name: "Faktura zaliczkowa", onClick: () => setOpen("ADVANCE") },
          {
            name: "Faktura zaliczkowa korygująca",
            onClick: () => setOpen("CORRECTION"),
          },
          { name: "Faktura końcowa", onClick: () => setOpen("FINAL") },
          ...(hasFinalOrVatInvoice
            ? [
                {
                  name: "Faktura końcowa korygująca",
                  onClick: () => {
                    setOpen("FINAL_CORRECTION");
                  },
                },
              ]
            : []),
        ]}
      >
        Faktura
      </ButtonWithContextMenu>
      <AdvanceOrProformaInvoiceDialog
        open={open === "ADVANCE" || open === "PROFORMA"}
        type={open === "PROFORMA" ? "PROFORMA" : "ADVANCE"}
        orderId={order.id}
        currency={order.currency}
        defaultAmount={order.advance.amount}
        onClose={() => setOpen(null)}
        onSubmit={open === "ADVANCE" ? handleSubmitAdvanceInvoice : handleSubmitProformaInvoice}
      />
      <CorrectionInvoiceDialog
        open={open === "CORRECTION"}
        orderId={order.id}
        currency={order.currency}
        onClose={() => setOpen(null)}
        onSubmit={handleSubmitCorrectionInvoice}
      />
      <FinalInvoiceDialog
        open={open === "FINAL"}
        orderId={order.id}
        currency={order.currency}
        onClose={() => setOpen(null)}
        onSubmit={handleSubmitFinalInvoice}
      />
      <FinalCorrectionInvoiceDialog
        open={open === "FINAL_CORRECTION"}
        orderId={order.id}
        currency={order.currency}
        onClose={() => setOpen(null)}
        onSubmit={handleSubmitFinalCorrectionInvoice}
      />
    </>
  );
};

export default OrderInvoiceButton;
